import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { openLink } from '@telegram-apps/sdk-react';
import axios from 'axios';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, type FieldErrors, type Resolver } from 'react-hook-form';
import { z } from 'zod';

import { logger } from '@app/logger';

import { useTelegramBackButton } from '@common/hooks';
import { useTabbar } from '@common/providers/useTabbar';
import { appInstanceService } from '@common/services/appInstanceService';
import { signInTma, AuthFailureReason } from '@common/services/tokenService';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Loader } from '@common/ui/Loader';
import { Steps } from '@common/ui/Steps';
import { Paragraph } from '@common/ui/typography/Paragraph';
import { useEventEmit } from '@common/utils/events';
import { getApiErrorInfo } from '@common/utils/fetcher';
import { OFERTA_URL, PRIVACY_POLICY_URL } from '@common/utils/legalLinks';
import { useRequestSubmit } from '@common/utils/requestSubmitPolyfill';
import { safeTma } from '@common/utils/safeTma';
import {
  nextRouteAfterAuth,
  readPendingInvite,
} from '@common/utils/startParam';

import { applyAuthResponse } from '@entities/account';
import { isStorableUser } from '@entities/auth/utils';

import { ACCOUNT_ERROR_CODES } from '@services/AccountService';
import { analytics, type SignupRole } from '@services/AnalyticsService';
import { type PostRegisterTmaParams } from '@services/AuthService';
import { usePostAuth, usePostRegisterTma } from '@services/hooks/AuthService';

import { About, BasicInfo, Pictures, Role } from './components';
import {
  describeInvalidFields,
  listNativeInvalidFields,
  NEXT_BUTTON_TEST_ID,
  useSignUpErrorLog,
  useSignUpStepLog,
} from './signUpDiagnostics';

const basicInfoSchema = z.object({
  name: z
    .string({ message: 'Введите имя' })
    .trim()
    .min(2, 'Не менее 2-х символов')
    .regex(/^[^\d]*$/, 'Не используйте цифры')
    .regex(/^[а-яёА-ЯЁ ]*$/, 'Только русские буквы')
    .regex(/^(?!.*[^аеёиоуыэюя]{4}).*$/i, 'Не более трех согласных подряд'),
  dateOfBirth: z
    .any({ message: 'Введите дату' })
    .refine((date) => {
      return date?.isValid();
    }, 'Введите корректную дату')
    .refine((date) => {
      const age = moment().diff(date, 'years');

      return age >= 18;
    }, 'Простите, но мы не пускаем людей младше 18 лет')
    .refine((date) => {
      const age = moment().diff(date, 'years');

      if (age < 0) {
        return false;
      }

      return age <= 80;
    }, 'Боимся, что вашему возрасту не подойдет наша аудитория'),
  gender: z.enum(['male', 'female']),
  goal: z.string({ message: 'Выберите цель' }).min(1, 'Выберите вариант'),
  city: z.string({ message: 'Выберите город' }).min(1, 'Выберите город'),
});

const lightBasicInfoSchema = basicInfoSchema.omit({ goal: true });

const SIGNUP_ROLES = ['dating', 'matchmaker', 'both'] as const;

const roleSchema = z.object({
  role: z.enum(SIGNUP_ROLES, { message: 'Выберите вариант' }),
});

const aboutSchema = z.object({
  about: z
    .string()
    .trim()
    .min(10, 'Не менее 10 символов')
    .refine(
      (val) => !/(https?:\/\/[^\s]+)/g.test(val),
      'Нельзя использовать ссылки',
    ),
});

const picturesSchema = z.object({
  pictures: z.array(z.object({ uploadURL: z.string() }), {
    required_error: 'Добавьте хотя бы одну фотографию',
  }),
});

type BasicInfoSchema = z.infer<typeof basicInfoSchema>;
type AboutSchema = z.infer<typeof aboutSchema>;
type PicturesSchema = z.infer<typeof picturesSchema>;

type FormData = BasicInfoSchema &
  AboutSchema &
  PicturesSchema & {
    /** Set only when the role step shows (matchmaker feature, no invite). */
    role?: SignupRole;
  };

export type SignUpFormData = FormData;

type SignUpStep = 'role' | 'basicInfo' | 'about' | 'pictures';

type SignUpResolverContext = {
  step: SignUpStep;
  isLight: boolean;
};

const getStepSchema = ({
  step,
  isLight,
}: SignUpResolverContext): z.ZodTypeAny => {
  switch (step) {
    case 'role':
      return roleSchema;
    case 'basicInfo':
      return isLight ? lightBasicInfoSchema : basicInfoSchema;
    case 'about':
      return aboutSchema;
    case 'pictures':
      return picturesSchema;
  }
};

/**
 * The sign-up's steps. The light (matchmaker) flow has no about step and no
 * goal; until a role is picked, the indicator counts the full flow.
 */
const getSteps = (isRoleStepShown: boolean, isLight: boolean): SignUpStep[] => [
  ...(isRoleStepShown ? (['role'] as const) : []),
  'basicInfo',
  ...(isLight ? [] : (['about'] as const)),
  'pictures',
];

/**
 * The role step shows only with the global matchmaker flag of
 * `/start/tma` and without a pending invite: an invitee must register for
 * dating to answer the candidate (plan S6). Flags known from this session
 * or a stored earlier answer decide at once (no loader); never-known flags
 * wait for `/start/tma` at most `START_FEATURES_WAIT_MS`. Unknown or absent
 * flags: no role step (today's sign-up).
 */
type RoleStepState = 'loading' | 'shown' | 'hidden';

const getInitialRoleStepState = (isInvitePending: boolean): RoleStepState => {
  if (isInvitePending) return 'hidden';

  const features = appInstanceService.getStartFeatures();

  if (features === undefined) return 'loading';

  return features?.matchmaker ? 'shown' : 'hidden';
};

const BOUND_APP_INSTANCE_ERROR_MESSAGE =
  'app_instance_id уже привязан к пользователю';

type RegisterTma = (payload: PostRegisterTmaParams) => Promise<unknown>;

const getProperty = (source: object, property: string): unknown =>
  Object.getOwnPropertyDescriptor(source, property)?.value;

const getResponseErrorMessages = (data: unknown): string[] => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const errors = getProperty(data, 'errors');
  const message = getProperty(data, 'message');
  const messages: string[] = [];

  if (errors && typeof errors === 'object') {
    const nestedMessage = getProperty(errors, 'message');

    if (typeof nestedMessage === 'string') {
      messages.push(nestedMessage);
    }
  }

  if (typeof message === 'string') {
    messages.push(message);
  }

  return messages;
};

const isBoundAppInstanceRegistrationError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return getResponseErrorMessages(error.response?.data).includes(
      BOUND_APP_INSTANCE_ERROR_MESSAGE,
    );
  }

  return (
    error instanceof Error && error.message === BOUND_APP_INSTANCE_ERROR_MESSAGE
  );
};

const buildRegisterTmaPayload = (
  data: FormData,
  appInstanceId: string,
): PostRegisterTmaParams => {
  const isMale = data.gender === 'male';
  // The picked calendar day as a plain date. toISOString() would send its
  // local midnight in UTC, which is the day before east of UTC (Moscow).
  const dob = data.dateOfBirth.format('YYYY-MM-DD');

  if (data.role === 'matchmaker') {
    return {
      mode: 'matchmaker',
      app_instance_id: appInstanceId,
      username: data.name,
      dob,
      sex: isMale ? 0 : 1,
      city: data.city,
      photos: [data.pictures[0]?.uploadURL || ''],
    };
  }

  return {
    app_instance_id: appInstanceId,
    username: data.name,
    dob,
    about: data.about,
    goal: data.goal,
    city: data.city,
    looking_for: isMale ? 1 : 0,
    sex: isMale ? 0 : 1,
    photos: data.pictures.map((picture) => picture.uploadURL || ''),
    ...(data.role === 'both' ? { is_matchmaker: true } : {}),
  };
};

const registerTmaWithFreshAppInstanceRetry = async ({
  data,
  register,
  getAppInstanceId,
  refreshAppInstanceId,
}: {
  data: FormData;
  register: RegisterTma;
  getAppInstanceId: () => Promise<string>;
  refreshAppInstanceId: () => Promise<string>;
}) => {
  const appInstanceId = await getAppInstanceId();

  try {
    await register(buildRegisterTmaPayload(data, appInstanceId));
  } catch (error) {
    if (!isBoundAppInstanceRegistrationError(error)) {
      throw error;
    }

    const freshAppInstanceId = await refreshAppInstanceId();
    await register(buildRegisterTmaPayload(data, freshAppInstanceId));
  }
};

export const SignUp = () => {
  const navigate = useNavigate();
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const { trigger: triggerPostAuth } = usePostAuth();

  const formRef = useRef<HTMLFormElement>(null);
  const isSubmittingSignUpRef = useRef(false);
  const [formData, setFormData] = useState<FormData>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isSubmittingSignUp, setIsSubmittingSignUp] = useState(false);
  const [, setErudaTapCount] = useState<number>(0);
  const [isInvitePending] = useState(() => readPendingInvite() !== null);
  const [roleStepState, setRoleStepState] = useState<RoleStepState>(() =>
    getInitialRoleStepState(isInvitePending),
  );

  useEffect(() => {
    if (isInvitePending) return;

    let isActive = true;

    // Answers at once from this session or a stored answer (refreshing the
    // stored one in the background), else within START_FEATURES_WAIT_MS.
    // Only a sign-up still waiting takes the answer: one on screen stays.
    void Promise.resolve(appInstanceService.loadStartFeatures()).then(
      (features) => {
        if (!isActive) return;

        setRoleStepState((state) => {
          if (state !== 'loading') return state;

          return features?.matchmaker ? 'shown' : 'hidden';
        });
      },
    );

    return () => {
      isActive = false;
    };
  }, [isInvitePending]);

  const handleBackClick = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  useTelegramBackButton({
    enabled: currentStep > 0,
    onClick: handleBackClick,
  });

  const {
    trigger: triggerPostRegisterTma,
    isMutating: isMutatingPostRegisterTma,
  } = usePostRegisterTma();

  useEffect(() => {
    analytics.trackSignUpStarted('initial');
  }, []);

  // The role counts once its step is submitted (into formData): until then
  // the indicator shows the full flow.
  const isRoleStepShown = roleStepState === 'shown';
  const isLight = isRoleStepShown && formData?.role === 'matchmaker';
  const steps = getSteps(isRoleStepShown, isLight);
  const step = steps[currentStep] ?? 'basicInfo';
  const isRoleStepLoading = roleStepState === 'loading';

  useSignUpErrorLog();
  useSignUpStepLog({
    step,
    index: currentStep,
    total: steps.length,
    isRoleStepShown,
    isLoading: isRoleStepLoading,
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData, SignUpResolverContext>({
    mode: 'onBlur',
    context: { step, isLight },
    defaultValues: {
      gender: 'male',
    },
    resolver: (async (data, context, options) => {
      const schema = getStepSchema(context ?? { step, isLight });
      const innerResolver = zodResolver<FormData>(
        schema as unknown as z.ZodType<FormData>,
      );
      return await innerResolver(data, context, options);
    }) satisfies Resolver<FormData, SignUpResolverContext>,
  });

  /**
   * The light registration answers 400 feature_disabled when the flag went
   * off meanwhile: the role step goes away and the dating flow starts over
   * at the basic info, which keeps what was entered.
   */
  const fallBackToDatingFlow = () => {
    setRoleStepState('hidden');
    setValue('role', undefined);
    setFormData((previous) => previous && { ...previous, role: undefined });
    setCurrentStep(0);
    emit('error', {
      title: 'Режим свахи сейчас недоступен',
      description: 'Продолжите регистрацию для знакомств',
    });
  };

  const submitSignUp = async (data: FormData) => {
    if (isSubmittingSignUpRef.current) return;

    isSubmittingSignUpRef.current = true;
    setIsSubmittingSignUp(true);

    try {
      await registerTmaWithFreshAppInstanceRetry({
        data,
        register: triggerPostRegisterTma,
        getAppInstanceId: appInstanceService.getOrCreateTmaAppInstanceId,
        refreshAppInstanceId: appInstanceService.refreshTmaAppInstanceId,
      });

      try {
        const signInResult = await signInTma();

        if (!signInResult.success) {
          throw signInResult.reason === AuthFailureReason.NETWORK_ERROR
            ? signInResult.error
            : new Error('Registered user sign-in failed');
        }

        const authData = await triggerPostAuth();

        // `trigger()` can resolve `undefined` (see isStorableUser), so the
        // response is checked before anything is read from it. The catch
        // below reports it and shows the profile error.
        if (!isStorableUser(authData?.user)) {
          throw new Error('POST /auth answered without a user (SignUp)');
        }

        // The account store too, so modes and features are known at once
        // (a light account starts with dating off).
        applyAuthResponse(authData);
      } catch (authError) {
        logger.captureException(authError, {
          tags: { section: 'SignUp post-registration auth' },
        });

        emit('error', {
          title: 'Не удалось загрузить профиль',
          description: 'Попробуйте перезагрузить приложение',
        });
        return;
      }

      analytics.trackSignUpCompleted('direct');

      if (data.role === 'matchmaker') {
        emit('info', {
          title: 'Анкета на проверке',
          description: 'Листать анкеты для друзей можно сразу после проверки',
        });
      }

      navigate({ to: nextRouteAfterAuth() });
    } catch (e) {
      const { status, code } = getApiErrorInfo(e);

      analytics.trackSignUpStep('register_failed', {
        status: status ?? 0,
        code: code ?? '',
        message: e instanceof Error ? e.message : '',
      });

      if (
        data.role === 'matchmaker' &&
        code === ACCOUNT_ERROR_CODES.featureDisabled
      ) {
        fallBackToDatingFlow();
        return;
      }

      logger.captureException(e, {
        tags: {
          section: 'Ошибка /Данные не загрузились/ при регистрации',
        },
      });
      emit('error', {
        title: 'Данные не загрузились',
        description: 'Попробуйте ещё раз',
      });
    } finally {
      isSubmittingSignUpRef.current = false;
      setIsSubmittingSignUp(false);
    }
  };

  const onInvalid = (invalidFields: FieldErrors<FormData>) => {
    analytics.trackSignUpStep('invalid', {
      step,
      ...describeInvalidFields(invalidFields),
    });
  };

  const onSubmit = async (data: FormData) => {
    const nextFormData = { ...formData, ...data };

    analytics.trackSignUpStep('valid', { step });
    setFormData(nextFormData);

    if (step === 'role' && nextFormData.role) {
      analytics.trackSignupRoleSelected(nextFormData.role);
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    await submitSignUp(nextFormData);
  };

  const requestSubmit = useRequestSubmit(formRef);

  useEffect(() => {
    if (isRoleStepLoading) {
      tabbar.setBottomSheet(undefined);
      return;
    }

    tabbar.setBottomSheet(
      <BottomSheet
        buttons={[
          <Button
            testId={NEXT_BUTTON_TEST_ID}
            loading={isMutatingPostRegisterTma || isSubmittingSignUp}
            disabled={isMutatingPostRegisterTma || isSubmittingSignUp}
            onClick={() => {
              analytics.trackSignUpStep('next_pressed', {
                step,
                has_form: formRef.current !== null,
                native_invalid: listNativeInvalidFields(formRef.current),
              });
              requestSubmit();
            }}
          >
            Далее
          </Button>,
        ]}
      />,
    );
  }, [
    currentStep,
    isMutatingPostRegisterTma,
    isRoleStepLoading,
    isSubmittingSignUp,
    requestSubmit,
    step,
    tabbar,
  ]);

  if (isRoleStepLoading) {
    return <Loader />;
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        gridGap: '12px',
      }}
    >
      <Steps
        testId="sign-up-steps"
        steps={steps.length}
        currentStep={currentStep + 1}
        onStepClick={(step) => {
          if (step !== 3) return;
          setErudaTapCount((prev) => {
            const next = prev + 1;
            if (next === 10) {
              //@ts-expect-error - eruda is a global debug library
              eruda.init();
              return 0;
            }
            return next;
          });
        }}
      />
      {step === 'role' && <Role control={control} errors={errors} />}
      {step === 'basicInfo' && (
        <BasicInfo control={control} errors={errors} isGoalShown={!isLight} />
      )}
      {step === 'about' && <About control={control} errors={errors} />}
      {step === 'pictures' && (
        <Pictures control={control} errors={errors} isSinglePhoto={isLight} />
      )}
      {/* Informational only — registration is not gated on these links. The
          documents are served by the backend outside the SPA, so open them
          externally. */}
      <Paragraph
        variant="secondary"
        align="center"
        style={{ fontSize: 12, lineHeight: '16px' }}
      >
        Регистрируясь, вы принимаете{' '}
        <a
          href={OFERTA_URL}
          // target=_blank is load-bearing, not decoration. When openLink
          // throws we deliberately let the browser follow the href — but an
          // in-place navigation inside the Telegram WebView would replace the
          // mini app with the legal page and destroy a half-filled
          // registration form, with no chrome to get back. Opening a new
          // context keeps the form alive.
          target="_blank"
          rel="noopener noreferrer"
          data-test-id="sign-up-oferta-link"
          style={{ color: 'inherit', textDecoration: 'underline' }}
          onClick={(event) => {
            // Only suppress native navigation if Telegram actually opened the
            // link; otherwise the href above is the fallback.
            if (safeTma('signUp.openLink.oferta', () => openLink(OFERTA_URL))) {
              event.preventDefault();
            }
          }}
        >
          условия использования
        </a>{' '}
        и{' '}
        <a
          href={PRIVACY_POLICY_URL}
          // See the oferta link above — target=_blank keeps the in-progress
          // registration form alive when the href fallback is used.
          target="_blank"
          rel="noopener noreferrer"
          data-test-id="sign-up-privacy-link"
          style={{ color: 'inherit', textDecoration: 'underline' }}
          onClick={(event) => {
            // See the oferta link above: native href is the fallback.
            if (
              safeTma('signUp.openLink.privacy', () =>
                openLink(PRIVACY_POLICY_URL),
              )
            ) {
              event.preventDefault();
            }
          }}
        >
          политику конфиденциальности
        </a>
      </Paragraph>
    </form>
  );
};
